import type { ReadObservation } from './bonus-retry-policy.ts';

/**
 * CF-BONUS Ticket B — client parser for the `GET /api/v1/bonus` v1 contract.
 *
 * Pure: no React, no window, no fetch. Unit-testable in plain Node, same shape as i18n/core.
 *
 * Scorer's binding amendments (1340) live HERE, not in the components, so a component cannot
 * accidentally bypass them:
 *
 *  A1 — a failed/unknown read is `unavailable`, NEVER `none`. Showing "no bonus" to a player
 *       who actually holds chips would read as their money vanishing. `none` is a FACT from
 *       the server; `unavailable` is our ignorance. They must never be conflated.
 *  A3 — nano values stay decimal STRINGS and are compared/derived with BigInt. Nothing here
 *       returns a JS `number` for money. Display clamping happens on the derived percentage
 *       only, never on a source value.
 *  A4 — require `contract_version === 1` AND a known state; anything else → `unavailable`.
 *  A2 — the caller may transition active→expired locally at `expires_at`, but this module
 *       never fabricates `terminal_at` or a conversion.
 */

export type BonusView =
  | { kind: 'unavailable'; reason: 'http' | 'network' | 'contract' | 'auth' }
  | { kind: 'none'; offerAvailable: boolean }
  | {
      kind: 'active';
      issuedNano: string;
      stickyNano: string;
      turnoverNano: string;
      wrTargetNano: string;
      expiresAt: number;
    }
  | {
      kind: 'terminal';
      state: 'converted' | 'expired' | 'voided';
      issuedNano: string;
      convertedNano: string;
      /** null for the read-time expired projection (sweeper has not finalized it yet). */
      terminalAt: number | null;
      /** true only for the derived expired-but-unswept projection. */
      isReadTimeProjection: boolean;
    };

/**
 * CF-BONUS-SETTLED-UNAVAILABLE-RESILIENCE-V1 — the discriminated read outcome.
 *
 * ⛔ WHY THIS LIVES HERE AND WHY IT IS NOT OPTIONAL. The owner's transport previously returned
 * `{ observation, view: BonusView }` for every observation, which makes
 * `{ observation: {kind:'network'}, view: <an ACTIVE bonus> }` CONSTRUCTIBLE — and the coordinator
 * would then emit `{phase:'retry_scheduled', bootstrapped:false, view:active}`, an illegal snapshot
 * under R5 I1/I3 that would let the panel render an unbootstrapped real view.
 *
 * The production transport happens never to build that pair, but the accepted design made it
 * STRUCTURALLY IMPOSSIBLE rather than conventionally avoided. I originally declared leaving this
 * file unmodified a benign path omission on the grounds that "classification lives in the policy
 * module" — that explanation was about the wrong thing. Classification was never why this file was
 * frozen for modification; the impossible-pair invariant was.
 */
export type RealBonusView = Exclude<BonusView, { kind: 'unavailable' }>;
export type UnavailableView = Extract<BonusView, { kind: 'unavailable' }>;

/**
 * A successful read carries a REAL view; a failed read carries an UNAVAILABLE view. No crossover.
 *
 * ⛔ ALL THREE FIELDS ARE CORRELATED, and the first version of this type was NOT. It read
 * `ReadOutcome<Obs>` with the same unrestricted `Obs` on both arms, which left BOTH cross-class
 * pairs constructible without a cast:
 *     { ok: true,  observation: {kind:'network'}, view: <ACTIVE> }
 *     { ok: false, observation: {kind:'ok'},      view: <UNAVAILABLE> }
 * Those drive `phase=retry_scheduled, bootstrapped=false, view=active` and
 * `phase=settled, bootstrapped=true, view=unavailable` — two illegal snapshots. I asserted the pair
 * was unconstructible; it compiled. A discriminant that nothing narrows on is decoration.
 */
export type ReadOutcome =
  | { ok: true;  observation: { kind: 'ok' };                            view: RealBonusView }
  | { ok: false; observation: Exclude<ReadObservation, { kind: 'ok' }>;  view: UnavailableView };

/** Canonical non-negative decimal nano string, mirroring the backend validator. */
function isNano(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0 && v.length <= 32 && /^[0-9]+$/.test(v);
}

function isEpoch(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0;
}

/**
 * Parse a successful JSON body. Any deviation from the contract yields `unavailable` rather
 * than a guess — an unknown shape is ignorance, not absence (A1/A4).
 */
/**
 * The exact `qualification_note` the v1 contract emits with `state:"none"`.
 * Mirrors `backend/src/custodial/routes/bonus.ts:31`. A `none` body without it is not a v1
 * body, and guessing that it means "no bonus" would be exactly the A1 mistake.
 */
export const QUALIFICATION_NOTE = 'final_eligibility_determined_at_deposit_credit_time';

/** The read-time expired projection marker. Valid on ONE shape and no other. */
const PROJECTION_MARKER = 'read_time_pending_sweeper_finalization';

/**
 * Parse a successful JSON body against the EXACT v1 per-state contract.
 *
 * Strictness is the point: every state has a required field set, and a field that belongs to a
 * different shape is a contradiction rather than a harmless extra. Scorer R3 found the earlier
 * version accepted a `projection` marker on a REAL terminal state — which would have rendered a
 * finalised, swept bonus as though the sweeper had not run yet.
 *
 * Per-state requirements, mirroring `routes/bonus.ts`:
 *   none      → offer_available:boolean, qualification_note === QUALIFICATION_NOTE
 *   active    → issued/sticky/turnover/wr_target nano, expires_at epoch; NO terminal_at, NO projection
 *   converted → issued/converted nano, expires_at epoch, terminal_at EPOCH; NO projection
 *   expired   → issued/converted/sticky nano, expires_at epoch, AND EITHER
 *                 terminal_at === null WITH projection === MARKER   (read-time projection), OR
 *                 terminal_at epoch WITHOUT projection              (swept)
 *   voided    → issued/converted/sticky nano, expires_at epoch, terminal_at EPOCH; NO projection
 *
 * Anything else → `unavailable`, never a guess (A1/A4).
 */
export function parseBonusBody(body: unknown): BonusView {
  const bad: BonusView = { kind: 'unavailable', reason: 'contract' };
  if (typeof body !== 'object' || body === null) return bad;
  const b = body as Record<string, unknown>;
  if (b.contract_version !== 1) return bad;

  const hasProjection = b.projection !== undefined;
  const projectionValid = b.projection === PROJECTION_MARKER;

  switch (b.state) {
    case 'none':
      if (typeof b.offer_available !== 'boolean') return bad;
      if (b.qualification_note !== QUALIFICATION_NOTE) return bad;
      return { kind: 'none', offerAvailable: b.offer_available };

    case 'active':
      if (!isNano(b.issued_nano) || !isNano(b.sticky_nano) || !isNano(b.turnover_nano)
          || !isNano(b.wr_target_nano) || !isEpoch(b.expires_at)) return bad;
      // An active bonus is not terminal and is not a projection of one.
      if (hasProjection || b.terminal_at !== undefined) return bad;
      return {
        kind: 'active',
        issuedNano: b.issued_nano,
        stickyNano: b.sticky_nano,
        turnoverNano: b.turnover_nano,
        wrTargetNano: b.wr_target_nano,
        expiresAt: b.expires_at,
      };

    case 'converted':
    case 'expired':
    case 'voided': {
      if (!isNano(b.issued_nano) || !isNano(b.converted_nano)) return bad;
      if (!isEpoch(b.expires_at)) return bad;
      // sticky_nano accompanies the chip-bearing terminals; converted does not carry it.
      if (b.state !== 'converted' && !isNano(b.sticky_nano)) return bad;

      const t = b.terminal_at;
      if (t === null) {
        // The ONLY legal null-terminal shape: the read-time expired projection.
        if (b.state !== 'expired' || !projectionValid) return bad;
        return {
          kind: 'terminal', state: 'expired',
          issuedNano: b.issued_nano, convertedNano: b.converted_nano,
          terminalAt: null, isReadTimeProjection: true,
        };
      }
      if (!isEpoch(t)) return bad;
      // A REAL terminal state must not claim to be a pending projection.
      if (hasProjection) return bad;
      return {
        kind: 'terminal', state: b.state,
        issuedNano: b.issued_nano, convertedNano: b.converted_nano,
        terminalAt: t, isReadTimeProjection: false,
      };
    }

    default:
      return bad; // unknown state → ignorance, never `none`
  }
}

/** Map a transport outcome to a view. A1: failures are never `none`. */
export function bonusViewFromStatus(status: number): UnavailableView {
  if (status === 401) return { kind: 'unavailable', reason: 'auth' };
  return { kind: 'unavailable', reason: 'http' };
}

export const NETWORK_UNAVAILABLE: UnavailableView = { kind: 'unavailable', reason: 'network' };

/**
 * Wagering progress as an integer percent 0..100, computed in BigInt (A3).
 *
 * Clamping applies to the DISPLAY percentage only — source nano values are never altered.
 * A zero/absent target yields 0 rather than dividing by zero.
 */
export function wagerProgressPercent(turnoverNano: string, wrTargetNano: string): number {
  if (!isNano(turnoverNano) || !isNano(wrTargetNano)) return 0;
  const target = BigInt(wrTargetNano);
  if (target === 0n) return 0;
  const done = BigInt(turnoverNano);
  const pct = (done * 100n) / target;
  if (pct <= 0n) return 0;
  if (pct >= 100n) return 100;
  return Number(pct); // safe: bounded 0..100 by construction
}

/** Remaining eligible turnover in nano, as a decimal string. Never negative. */
export function remainingTurnoverNano(turnoverNano: string, wrTargetNano: string): string {
  if (!isNano(turnoverNano) || !isNano(wrTargetNano)) return '0';
  const target = BigInt(wrTargetNano);
  const done = BigInt(turnoverNano);
  return (done >= target ? 0n : target - done).toString();
}

/**
 * A2: the countdown may present an active bonus as expired once the clock passes
 * `expires_at`, WITHOUT inventing a terminal timestamp or a conversion outcome.
 */
export function isVisuallyExpired(view: BonusView, nowSeconds: number): boolean {
  return view.kind === 'active' && nowSeconds >= view.expiresAt;
}

/** Whole seconds until expiry, floored at 0. Display helper only. */
export function secondsUntilExpiry(expiresAt: number, nowSeconds: number): number {
  const d = expiresAt - nowSeconds;
  return d > 0 ? d : 0;
}
